import React, { useEffect, useState, useRef } from 'react';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import Card, { CardActions, CardBody, CardHeader, CardLabel, CardTitle } from '../bootstrap/Card';
import FormGroup from '../bootstrap/forms/FormGroup';
import Input from '../bootstrap/forms/Input';
import Button, { ButtonGroup } from '../bootstrap/Button';
import { getCurrentDateAndTime } from '../../services/common.service';
import { getProductStockReport } from '../../services/reportService';
import { toasts } from '../../services/toaste.service';
import Icon from '../icon/Icon';
import dayjs from 'dayjs';
import Collapse from '../bootstrap/Collapse';
import OpenCardComponent from '../../common/components/OpenCardComponent';
import useSortableData from '../../hooks/useSortableData';
import PaginationButtons, { dataPagination } from '../PaginationButtons';
import ExportDropdown from '../../common/components/ExportDropdown';
import { getSubModuleType } from '../../services/productService';
import SearchableSelect from '../../common/components/SearchableSelect';
import { useReactToPrint } from 'react-to-print';


const UnscannedProductReport = () => {
    const [fromDate, setFromDate] = useState<string>(getCurrentDateAndTime('date'));
    const [toDate, setToDate] = useState<string>(getCurrentDateAndTime('date'));
    const [reportData, setReportData] = useState<any[]>([]);
    const [isLoader, setIsLoader] = useState<boolean>(false);
    const [dataStatus, setDataStatus] = useState<boolean>(false);
    const [noDataMsg, setNoDataMsg] = useState<string>('');
    const [isOpenListCard, setIsOpenListCard] = useState<boolean>(true);
    const [searchInput, setSearchInput] = useState<string>('');

    const [lotList, setLotList] = useState<any[]>([]);
    const [countList, setCountList] = useState<any[]>([]);
    const [lotId, setLotId] = useState<any>({ value: 0, label: 'All' });
    const [countId, setCountId] = useState<any>(null);

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
        contentRef: componentRef,
    });

    useEffect(() => {
        getSubModuleType(1, (res) => {
            if (res?.data?.success && res?.data?.data?.subModuleType) {
                const data = res.data.data.subModuleType;
                const formatted = data.map((i: any) => ({ value: i.subModuleTypeId, label: i.subModuleType }));
                setLotList(formatted);
                if (formatted.length === 0) {
                    setLotId(null);
                }
            } else {
                setLotList([]);
                setLotId(null);
            }
        }, (err) => toasts(err, "Error"));

        getSubModuleType(2, (res) => {
            if (res?.data?.success && res?.data?.data?.subModuleType) {
                const data = res.data.data.subModuleType;
                const formatted = data.map((i: any) => ({ value: i.subModuleTypeId, label: i.subModuleType }));
                setCountList(formatted);
                if (formatted.length === 0) {
                    setCountId(null);
                } else {
                    setCountId({ value: 0, label: 'All' });
                }
            } else {
                setCountList([]);
                setCountId(null);
            }
        }, (err) => toasts(err, "Error"));
    }, []);

    const handleLotChange = (val: any) => {
        const selectedLot = val || { value: 0, label: 'All' };
        setLotId(selectedLot);

        if (selectedLot.value === 0) {
            setCountId({ value: 0, label: 'All' });
        } else {
            if (countList.length > 0) {
                setCountId({ value: 0, label: 'All' });
            } else {
                setCountId(null);
            }
        }
    };

    const rawFiltered = reportData.filter((item: any) =>
        item.lotNo?.toLowerCase().includes(searchInput.toLowerCase()) ||
        item.countNo?.toLowerCase().includes(searchInput.toLowerCase()) ||
        item.bagNo?.toLowerCase().includes(searchInput.toLowerCase()) ||
        item.exciseNo?.toLowerCase().includes(searchInput.toLowerCase()) ||
        item.blend?.toLowerCase().includes(searchInput.toLowerCase())
    );

    const getGroupedData = (data: any[]) => {
        const groupedMap = new Map<string, any>();

        for (let i = 0; i < data.length; i++) {
            const item = data[i];
            const key = `${item.lotId}-${item.countId}`;
            if (!groupedMap.has(key)) {
                groupedMap.set(key, {
                    lotId: item.lotId,
                    lotNo: item.lotNo,
                    countId: item.countId,
                    countNo: item.countNo,
                    blend: item.blend,
                    noOfCone: item.noOfCone,
                    coneWeight: item.coneWeight,
                    duplicateFlag: item.duplicateFlag,
                    productList: [],
                });
            }
            groupedMap.get(key).productList.push(item);
        }

        const flattenedData: any[] = [];
        groupedMap.forEach((group: any) => {
            group.productList.sort((a: any, b: any) => {
                const aNum = Number(a.bagNo) || 0;
                const bNum = Number(b.bagNo) || 0;
                return aNum - bNum;
            });

            for (let k = 0; k < group.productList.length; k++) {
                group.productList[k].sno = k + 1;
            }

            if (group.productList.length > 0) {
                group.fromBagNo = group.productList[0].bagNo;
                group.toBagNo = group.productList[group.productList.length - 1].bagNo;
            } else {
                group.fromBagNo = '';
                group.toBagNo = '';
            }

            group.totalGrossWeight = group.productList.reduce((s: any, r: any) => s + Number(r.grossWeight || 0), 0);
            group.totalTareWeight = group.productList.reduce((s: any, r: any) => s + Number(r.tareWeight || 0), 0);
            group.totalNetWeight = group.productList.reduce((s: any, r: any) => s + Number(r.netWeight || 0), 0);

            const rowsPerTable = Math.ceil(group.productList.length / 2);
            const totalRequired = rowsPerTable * 2;
            let paddedRows: any = [...group.productList];

            while (paddedRows.length < totalRequired) {
                paddedRows.push({ sno: '', bagNo: "", exciseNo: "", grossWeight: "", tareWeight: "", netWeight: "" });
            }

            const left = paddedRows.slice(0, rowsPerTable);
            const right = paddedRows.slice(rowsPerTable);

            group.leftRows = left;
            group.rightRows = right;

            const totals = (rows: any) =>
                rows.reduce((a: any, r: any) => ({
                    gross: a.gross + Number(r.grossWeight || 0),
                    tare: a.tare + Number(r.tareWeight || 0),
                    net: a.net + Number(r.netWeight || 0),
                }), { gross: 0, tare: 0, net: 0 });

            group.leftTotals = totals(left);
            group.rightTotals = totals(right);

            flattenedData.push(group);
        });

        return flattenedData;
    };

    const groupedReportData = getGroupedData(rawFiltered);

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const { items, requestSort, getClassNamesFor } = useSortableData(groupedReportData);
    const onCurrentPageData = dataPagination(items, currentPage, perPage);

    const columnVisibilityData = [
        { columnName: "S.No", keyName: "sno" },
        { columnName: "Lot No", keyName: "lotNo" },
        { columnName: "Count No", keyName: "countNo" },
        { columnName: "S.Color", keyName: "blend" },
        { columnName: "No of Cone", keyName: "noOfCone" },
        { columnName: "Cone Wt", keyName: "coneWeight" },
        { columnName: "Excise No", keyName: "exciseNo" },
        { columnName: "Bag No", keyName: "bagNo" },
        { columnName: "Tare Wt", keyName: "tareWeight" },
        { columnName: "Net Wt", keyName: "netWeight" },
        { columnName: "Gross Wt", keyName: "grossWeight" },
        { columnName: "Product Date", keyName: "productDateForView" },
    ];

    const viewReport = () => {
        setIsLoader(true);
        setDataStatus(false);
        setReportData([]);
        setNoDataMsg('');

        const fromDateMs = dayjs(fromDate).startOf('day').valueOf();
        const toDateMs = dayjs(toDate).endOf('day').valueOf();
        const lId = lotId?.value || 0;
        const cId = countId?.value || 0;

        getProductStockReport(
            fromDateMs,
            toDateMs,
            lId,
            cId,
            (response) => {
                setIsLoader(false);
                if (response.data.success) {
                    const data = response.data.data.productStockReport;
                    if (data && data.length > 0) {
                        for (let i = 0; i < data.length; i++) {
                            data[i].sno = i + 1;
                        }
                        setReportData(data);
                        setDataStatus(true);
                        setIsOpenListCard(false);
                    } else {
                        setNoDataMsg(data.message);
                        setDataStatus(true);
                        setIsOpenListCard(false);
                    }
                } else {
                    setNoDataMsg(response.data.message);
                    setDataStatus(true);
                    setIsOpenListCard(false);
                }
            },
            (error) => {
                setIsLoader(false);
                toasts(error, 'Error');
                setNoDataMsg('An error occurred while fetching the report.');
                setDataStatus(true);
                setIsOpenListCard(false);
            }
        );
    };

    return (
        <PageWrapper title='Unscanned Product Report'>
            <Page>
                <Collapse isOpen={isOpenListCard}>
                    <Card stretch className='d-print-none mb-0'>
                        <CardHeader borderSize={1}>
                            <CardLabel icon='Inventory' iconColor='info'>
                                <CardTitle>Unscanned Product Report (Stock)</CardTitle>
                            </CardLabel>
                        </CardHeader>
                        <CardBody>
                            <div className='row g-4'>
                                <div className='col-lg-2'>
                                    <FormGroup id='fromDate' label='From Date' isFloating>
                                        <Input
                                            type='date'
                                            placeholder='From Date'
                                            onChange={(e: any) => setFromDate(e.target.value)}
                                            value={fromDate}
                                            max={getCurrentDateAndTime('date')}
                                        />
                                    </FormGroup>
                                </div>
                                <div className='col-lg-2'>
                                    <FormGroup id='toDate' label='To Date' isFloating>
                                        <Input
                                            type='date'
                                            placeholder='To Date'
                                            onChange={(e: any) => setToDate(e.target.value)}
                                            value={toDate}
                                            max={getCurrentDateAndTime('date')}
                                        />
                                    </FormGroup>
                                </div>
                                <div className='col-lg-3'>
                                    <FormGroup id='lot' label='Lot No' isFloating>
                                        <SearchableSelect
                                            isFloating
                                            id='lot'
                                            ariaLabel='Lot'
                                            placeholder='Select Lot'
                                            value={lotId}
                                            onChange={handleLotChange}
                                            list={lotList.length > 0 ? [{ value: 0, label: 'All' }, ...lotList] : []}
                                        />
                                    </FormGroup>
                                </div>
                                <div className='col-lg-3'>
                                    <FormGroup id='count' label='Count No' isFloating>
                                        <SearchableSelect
                                            isFloating
                                            id='count'
                                            ariaLabel='Count'
                                            placeholder='Select Count'
                                            value={countId}
                                            onChange={(val: any) => setCountId(val || { value: 0, label: 'All' })}
                                            list={countList.length > 0 ? [{ value: 0, label: 'All' }, ...countList] : []}
                                        />
                                    </FormGroup>
                                </div>
                                <div className='col-lg-2 d-flex align-items-center mt-4'>
                                    <Button
                                        color='primary'
                                        icon='Search'
                                        onClick={viewReport}
                                        className='mt-2'>
                                        View
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Collapse>

                <div className="mt-3"></div>

                <OpenCardComponent isOpenListCard={isOpenListCard} setIsOpenListCard={setIsOpenListCard} isLoader={isLoader} />

                {dataStatus && (
                    <Card stretch className='shadow-sm'>
                        <CardHeader borderSize={1}>
                            <CardLabel icon='List' iconColor='primary'>
                                <CardTitle>Report Data</CardTitle>
                            </CardLabel>
                            <CardActions>
                                <div className='d-flex align-items-center gap-2'>
                                    <ButtonGroup className='d-print-none' color='primary'>
                                        <Icon className='mt-1' icon='Search' size='2x' color='primary' />
                                        <Input
                                            id='tableSearch'
                                            type='search'
                                            placeholder='Search...'
                                            onChange={(e: any) => setSearchInput(e.target.value)}
                                            value={searchInput}
                                        />
                                    </ButtonGroup>
                                    <ExportDropdown
                                        fileName='Unscanned_Product_Report'
                                        reportData={reportData}
                                        columnVisibilityData={columnVisibilityData}
                                        customPrint={handlePrint}
                                    />
                                </div>
                            </CardActions>
                        </CardHeader>
                        <CardBody className='p-3'>
                            {onCurrentPageData.length > 0 ? (
                                <div className='p-0' id="print-container" ref={componentRef}>
                                    <style dangerouslySetInnerHTML={{
                                        __html: `
                                        /* Web-specific styling */
                                        .web-block-card {
                                            background: #ffffff;
                                            border: 1px solid rgba(0, 0, 0, 0.05);
                                            border-radius: 12px;
                                            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
                                            padding: 14px 18px;
                                            margin-bottom: 16px;
                                            transition: transform 0.2s ease, box-shadow 0.2s ease;
                                        }
                                        .web-block-card:hover {
                                            transform: translateY(-2px);
                                            box-shadow: 0 10px 25px rgba(79, 70, 229, 0.08);
                                        }
                                        .card-header-badge-container {
                                            display: flex;
                                            flex-wrap: wrap;
                                            gap: 6px;
                                            align-items: center;
                                            margin-top: 6px;
                                        }
                                        .badge-pill-modern {
                                            font-size: 10px;
                                            font-weight: 600;
                                            padding: 4px 10px;
                                            border-radius: 30px;
                                            letter-spacing: 0.3px;
                                            display: inline-flex;
                                            align-items: center;
                                            gap: 6px;
                                            border: 1px solid transparent;
                                        }
                                        .badge-pill-lot {
                                            background-color: rgba(79, 70, 229, 0.08);
                                            color: #4f46e5;
                                            border-color: rgba(79, 70, 229, 0.15);
                                        }
                                        .badge-pill-count {
                                            background-color: rgba(16, 185, 129, 0.08);
                                            color: #10b981;
                                            border-color: rgba(16, 185, 129, 0.15);
                                        }
                                        .badge-pill-blend {
                                            background-color: rgba(6, 182, 212, 0.08);
                                            color: #06b6d4;
                                            border-color: rgba(6, 182, 212, 0.15);
                                        }
                                        .badge-pill-cone {
                                            background-color: rgba(245, 158, 11, 0.08);
                                            color: #d97706;
                                            border-color: rgba(245, 158, 11, 0.15);
                                        }
                                        .badge-pill-duplicate {
                                            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                                            color: #ffffff;
                                            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
                                            animation: pulse-danger-unscanned 2.5s infinite;
                                        }
                                        @keyframes pulse-danger-unscanned {
                                            0% { transform: scale(1); }
                                            50% { transform: scale(1.03); }
                                            100% { transform: scale(1); }
                                        }
                                        .web-table-container {
                                            background: #fbfbfb;
                                            border-radius: 8px;
                                            overflow: hidden;
                                            border: 1px solid rgba(0, 0, 0, 0.05);
                                            margin-top: 12px;
                                        }
                                        .web-table {
                                            margin-bottom: 0 !important;
                                            width: 100%;
                                            border-collapse: collapse;
                                        }
                                        .web-table th {
                                            background-color: #f3f4f6 !important;
                                            color: #4b5563 !important;
                                            font-weight: 600;
                                            text-transform: uppercase;
                                            font-size: 10px;
                                            letter-spacing: 0.5px;
                                            border-bottom: 2px solid #e5e7eb !important;
                                            padding: 6px 8px !important;
                                            text-align: center;
                                        }
                                        .web-table td {
                                            padding: 5px 8px !important;
                                            font-size: 12px;
                                            color: #374151;
                                            border-bottom: 1px solid #f3f4f6;
                                            vertical-align: middle;
                                            text-align: center;
                                            border-right: 1px solid rgba(0, 0, 0, 0.03);
                                        }
                                        .web-table td:last-child {
                                            border-right: none;
                                        }
                                        .web-table tbody tr:hover {
                                            background-color: rgba(249, 250, 251, 0.8) !important;
                                        }
                                        .web-totals-row {
                                            background-color: rgba(79, 70, 229, 0.04) !important;
                                            font-weight: 700;
                                        }
                                        .web-totals-row td {
                                            color: #4f46e5 !important;
                                            border-top: 2px solid rgba(79, 70, 229, 0.15) !important;
                                            font-size: 12px !important;
                                        }
                                         .dashboard-grid {
                                             display: flex;
                                             flex-wrap: wrap;
                                             align-items: center;
                                             justify-content: space-between;
                                             gap: 8px;
                                             margin-top: 12px;
                                             border: 1px solid rgba(0, 0, 0, 0.05);
                                             background: rgba(249, 250, 251, 0.85);
                                             border-radius: 8px;
                                             padding: 8px 16px;
                                         }
                                         .dashboard-tile {
                                             flex: 1;
                                             min-width: 140px;
                                             display: flex;
                                             flex-direction: column;
                                             align-items: center;
                                             justify-content: center;
                                             text-align: center;
                                             padding: 2px 10px;
                                             border-right: 1px solid rgba(0, 0, 0, 0.08);
                                         }
                                         .dashboard-tile:last-child {
                                             border-right: none;
                                         }
                                         .dashboard-tile-label {
                                             font-size: 10px;
                                             font-weight: 600;
                                             color: #6b7280;
                                             text-transform: uppercase;
                                             letter-spacing: 0.5px;
                                             margin-bottom: 2px;
                                         }
                                         .dashboard-tile-value {
                                             font-size: 13px;
                                             font-weight: 700;
                                             color: #111827;
                                         }
                                         .dashboard-tile-value-highlight {
                                             color: #4f46e5;
                                         }
                                         .dashboard-tile-value-success {
                                             color: #10b981;
                                         }
                                         .dashboard-tile-value-info {
                                             color: #06b6d4;
                                         }
                                         @media (max-width: 768px) {
                                             .dashboard-tile {
                                                 border-right: none;
                                                 border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                                                 padding-bottom: 6px;
                                             }
                                             .dashboard-tile:last-child {
                                                 border-bottom: none;
                                                 padding-bottom: 0;
                                             }
                                         }

                                        /* Print styles overrides */
                                        .print-only-layout {
                                            display: none !important;
                                        }

                                        @media print {
                                            @page {
                                                margin: 5mm;
                                            }
                                            .web-block-card {
                                                display: none !important;
                                            }
                                            .print-only-layout {
                                                display: block !important;
                                            }
                                            .print-compact-table {
                                                width: 100% !important;
                                                border-collapse: collapse !important;
                                            }
                                            .print-compact-table th, 
                                            .print-compact-table td {
                                                padding: 3px 3px !important;
                                                font-size: 11px !important;
                                                line-height: 1 !important;
                                                height: auto !important;
                                            }
                                            .print-compact-table h5 {
                                                font-size: 14px !important;
                                                margin: 5px 0 !important;
                                            }
                                            .print-compact-table b {
                                                font-size: 10px !important;
                                            }
                                            #print-container {
                                                overflow: visible !important;
                                                height: auto !important;
                                                padding: 0 !important;
                                            }
                                        }
                                    `}} />
                                    {onCurrentPageData.map((item: any, index: number) => {
                                        return (
                                            <div key={index} style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }} className='mb-4'>

                                                {/* WEB INTERACTIVE VIEW */}
                                                <div className="web-block-card d-print-none">
                                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                                        <div>
                                                            <div className="text-muted fw-semibold" style={{ fontSize: '12px' }}>
                                                                Report Period: {dayjs(fromDate).format('DD/MM/YYYY')} to {dayjs(toDate).format('DD/MM/YYYY')}
                                                            </div>
                                                        </div>
                                                        <div className="card-header-badge-container">
                                                            <span className="badge-pill-modern badge-pill-lot">
                                                                <Icon icon="Pin" /> Lot No : {item.lotNo}
                                                            </span>
                                                            <span className="badge-pill-modern badge-pill-count">
                                                                {(item.duplicateFlag === true || item.duplicateFlag == 1) ? (
                                                                    <Icon icon="Warning" color="danger" />
                                                                ) : (
                                                                    <Icon icon="Numbers" />
                                                                )}
                                                                Count No : {item.countNo}
                                                            </span>
                                                            <span className="badge-pill-modern badge-pill-blend">
                                                                <Icon icon="Palette" />S.Color : {item.blend || '-'}
                                                            </span>
                                                            <span className="badge-pill-modern badge-pill-cone">
                                                                <Icon icon="Scale" /> {item.noOfCone} Cones ({item.coneWeight} kg)
                                                            </span>
                                                            {(item.duplicateFlag === true || item.duplicateFlag == 1) && (
                                                                <span className="badge-pill-modern badge-pill-duplicate">
                                                                    <Icon icon="Warning" /> Duplicate Stock Warning
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="web-table-container">
                                                        <table className="table web-table">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: '5%' }}>S.No</th>
                                                                    <th style={{ width: '10%' }}>Bag No</th>
                                                                    <th style={{ width: '10%' }}>Excise No</th>
                                                                    <th style={{ width: '8%' }}>Tare Wt</th>
                                                                    <th style={{ width: '8%' }}>Bag Wt</th>
                                                                    <th style={{ width: '9%' }}>Gross Wt</th>

                                                                    <th style={{ width: '5%' }}>S.No</th>
                                                                    <th style={{ width: '10%' }}>Bag No</th>
                                                                    <th style={{ width: '10%' }}>Excise No</th>
                                                                    <th style={{ width: '8%' }}>Tare Wt</th>
                                                                    <th style={{ width: '8%' }}>Bag Wt</th>
                                                                    <th style={{ width: '9%' }}>Gross Wt</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {item.leftRows.map((leftRow: any, i: any) => {
                                                                    const rightRow = item.rightRows[i];

                                                                    return (
                                                                        <tr key={i}>
                                                                            {/* LEFT SIDE */}
                                                                            <td className="fw-semibold text-muted">{leftRow.sno}</td>
                                                                            <td className="fw-bold text-dark">{leftRow.bagNo}</td>
                                                                            <td>{leftRow.exciseNo}</td>
                                                                            <td>{leftRow.tareWeight ? Number(leftRow.tareWeight).toFixed(3) : ''}</td>
                                                                            <td>{leftRow.netWeight ? Number(leftRow.netWeight).toFixed(3) : ''}</td>
                                                                            <td className="fw-semibold text-dark">{leftRow.grossWeight ? Number(leftRow.grossWeight).toFixed(3) : ''}</td>

                                                                            {/* RIGHT SIDE */}
                                                                            {rightRow ? (
                                                                                <>
                                                                                    <td className="fw-semibold text-muted">{rightRow.sno}</td>
                                                                                    <td className="fw-bold text-dark">{rightRow.bagNo}</td>
                                                                                    <td>{rightRow.exciseNo}</td>
                                                                                    <td>{rightRow.tareWeight ? Number(rightRow.tareWeight).toFixed(3) : ''}</td>
                                                                                    <td>{rightRow.netWeight ? Number(rightRow.netWeight).toFixed(3) : ''}</td>
                                                                                    <td className="fw-semibold text-dark">{rightRow.grossWeight ? Number(rightRow.grossWeight).toFixed(3) : ''}</td>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <td></td><td></td><td></td><td></td><td></td><td></td>
                                                                                </>
                                                                            )}
                                                                        </tr>
                                                                    );
                                                                })}
                                                                <tr className="web-totals-row">
                                                                    <td colSpan={3} className='fw-semibold text-dark'>Total</td>
                                                                    <td>{item?.leftTotals?.tare ? Number(item.leftTotals.tare).toFixed(3) : '0.000'}</td>
                                                                    <td>{item?.leftTotals?.net ? Number(item.leftTotals.net).toFixed(3) : '0.000'}</td>
                                                                    <td>{item?.leftTotals?.gross ? Number(item.leftTotals.gross).toFixed(3) : '0.000'}</td>
                                                                    <td colSpan={3} className='fw-semibold text-dark'>Total</td>
                                                                    <td>{item?.rightTotals?.tare ? Number(item.rightTotals.tare).toFixed(3) : '0.000'}</td>
                                                                    <td>{item?.rightTotals?.net ? Number(item.rightTotals.net).toFixed(3) : '0.000'}</td>
                                                                    <td>{item?.rightTotals?.gross ? Number(item.rightTotals.gross).toFixed(3) : '0.000'}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    <div className="dashboard-grid">
                                                        <div className="dashboard-tile">
                                                            <div className="dashboard-tile-label">Bag Range</div>
                                                            <div className="dashboard-tile-value dashboard-tile-value-info">
                                                                {item.fromBagNo || '-'} to {item.toBagNo || '-'}
                                                            </div>
                                                        </div>
                                                        <div className="dashboard-tile">
                                                            <div className="dashboard-tile-label">Total Bags</div>
                                                            <div className="dashboard-tile-value">
                                                                {item.productList?.length || 0}
                                                            </div>
                                                        </div>
                                                        <div className="dashboard-tile">
                                                            <div className="dashboard-tile-label">Total Tare Weight</div>
                                                            <div className="dashboard-tile-value text-muted">
                                                                {item.totalTareWeight ? Number(item.totalTareWeight).toFixed(3) : '0.000'} <span style={{ fontSize: '11px' }}>kg</span>
                                                            </div>
                                                        </div>
                                                        <div className="dashboard-tile">
                                                            <div className="dashboard-tile-label">Total Bag Wt</div>
                                                            <div className="dashboard-tile-value dashboard-tile-value-success">
                                                                {item.totalNetWeight ? Number(item.totalNetWeight).toFixed(3) : '0.000'} <span style={{ fontSize: '11px' }}>kg</span>
                                                            </div>
                                                        </div>
                                                        <div className="dashboard-tile">
                                                            <div className="dashboard-tile-label">Total Gross Weight</div>
                                                            <div className="dashboard-tile-value dashboard-tile-value-highlight">
                                                                {item.totalGrossWeight ? Number(item.totalGrossWeight).toFixed(3) : '0.000'} <span style={{ fontSize: '11px' }}>kg</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* HIGH COMPACT PRINT-ONLY GRID */}
                                                <div className="print-only-layout">
                                                    <table className="table table-responsive table-bordered border border-dark border-1 print-compact-table small" style={{ fontSize: '12px' }}>
                                                        <tbody>
                                                            <tr>
                                                                <td style={{ width: '34%' }} colSpan={5} className="align-middle p-2">
                                                                    <div className="pe-2 text-start" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                                                        <div className="fw-bold text-dark text-uppercase mt-1" style={{ fontSize: '12px', letterSpacing: '0.3px' }}>
                                                                            Unscanned Products Report
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="align-middle p-2" style={{ width: '33%' }} colSpan={4}>
                                                                    <div className="pe-2 text-center" style={{ fontSize: '11px', lineHeight: '1.4' }}>

                                                                        <div className="fw-bold text-dark text-uppercase mt-1" style={{ fontSize: '12px', letterSpacing: '0.3px' }}>From Date : &nbsp;
                                                                            {dayjs(fromDate).format('DD/MM/YYYY')}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="align-middle p-2" style={{ width: '33%' }} colSpan={4}>
                                                                    <div className="pe-2 text-end" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                                                        <div className="fw-bold text-dark text-uppercase mt-1" style={{ fontSize: '12px', letterSpacing: '0.3px' }}>To Date : &nbsp;
                                                                            {dayjs(toDate).format('DD/MM/YYYY')}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>

                                                            <tr>
                                                                <td style={{ lineHeight: '0.1', width: '50%' }} colSpan={12}>
                                                                    <div className="text-center font-semibold border-b border-black py-1 fw-bold mt-1" style={{ fontSize: '11px' }}>
                                                                        {(item.duplicateFlag === true || item.duplicateFlag == 1) && (
                                                                            <Icon icon='Warning' color='dark' className="me-1 align-middle" style={{ verticalAlign: 'middle' }} />
                                                                        )}
                                                                        {item.countNo} [ Lot No : &nbsp;{item.lotNo} ] [ No of Cone :&nbsp;{item.noOfCone} ] [ Cone Weight :&nbsp; {item.coneWeight} ]
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            <tr className='text-center fw-bold bg-light'>
                                                                <td style={{ width: '5%' }}>S.No</td>
                                                                <td className='border border-dark' style={{ width: '10%' }}>Bag No</td>
                                                                <td className='border border-dark' style={{ width: '10%' }}>Excise No</td>
                                                                <td className='border border-dark' style={{ width: '8%' }}>Tare Wt</td>
                                                                <td className='border border-dark' style={{ width: '8%' }}>Bag Wt</td>
                                                                <td className='border border-dark' style={{ width: '9%' }}>Gross Wt</td>

                                                                <td style={{ width: '5%' }}>S.No</td>
                                                                <td className='border border-dark' style={{ width: '10%' }}>Bag No</td>
                                                                <td className='border border-dark' style={{ width: '10%' }}>Excise No</td>
                                                                <td className='border border-dark' style={{ width: '8%' }}>Tare Wt</td>
                                                                <td className='border border-dark' style={{ width: '8%' }}>Bag Wt</td>
                                                                <td className='border border-dark' style={{ width: '9%' }}>Gross Wt</td>
                                                            </tr>

                                                            {item.leftRows.map((leftRow: any, i: any) => {
                                                                const rightRow = item.rightRows[i];

                                                                return (
                                                                    <tr className='text-center' key={i}>
                                                                        {/* LEFT SIDE */}
                                                                        <td className="p-1">{leftRow.sno}</td>
                                                                        <td className="border border-dark p-1">{leftRow.bagNo}</td>
                                                                        <td className="border border-dark p-1">{leftRow.exciseNo}</td>
                                                                        <td className="border border-dark p-1">{leftRow.tareWeight ? Number(leftRow.tareWeight).toFixed(3) : ''}</td>
                                                                        <td className="border border-dark p-1">{leftRow.netWeight ? Number(leftRow.netWeight).toFixed(3) : ''}</td>
                                                                        <td className="border border-dark p-1">{leftRow.grossWeight ? Number(leftRow.grossWeight).toFixed(3) : ''}</td>

                                                                        {/* RIGHT SIDE */}
                                                                        {rightRow ? (
                                                                            <>
                                                                                <td className="p-1">{rightRow.sno}</td>
                                                                                <td className="border border-dark p-1">{rightRow.bagNo}</td>
                                                                                <td className="border border-dark p-1">{rightRow.exciseNo}</td>
                                                                                <td className="border border-dark p-1">{rightRow.tareWeight ? Number(rightRow.tareWeight).toFixed(3) : ''}</td>
                                                                                <td className="border border-dark p-1">{rightRow.netWeight ? Number(rightRow.netWeight).toFixed(3) : ''}</td>
                                                                                <td className="border border-dark p-1">{rightRow.grossWeight ? Number(rightRow.grossWeight).toFixed(3) : ''}</td>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <td></td><td></td><td></td><td></td><td></td><td></td>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                );
                                                            })}
                                                            <tr className='text-center fw-bold bg-light border-top border-dark border-1'>
                                                                <td className="p-1" colSpan={3}>Total</td>
                                                                <td className="border border-dark p-1">{item?.leftTotals?.tare ? Number(item.leftTotals.tare).toFixed(3) : '0.000'}</td>
                                                                <td className="border border-dark p-1">{item?.leftTotals?.net ? Number(item.leftTotals.net).toFixed(3) : '0.000'}</td>
                                                                <td className="border border-dark p-1">{item?.leftTotals?.gross ? Number(item.leftTotals.gross).toFixed(3) : '0.000'}</td>
                                                                <td className="p-1" colSpan={3}>Total</td>
                                                                <td className="border border-dark p-1">{item?.rightTotals?.tare ? Number(item.rightTotals.tare).toFixed(3) : '0.000'}</td>
                                                                <td className="border border-dark p-1">{item?.rightTotals?.net ? Number(item.rightTotals.net).toFixed(3) : '0.000'}</td>
                                                                <td className="border border-dark p-1">{item?.rightTotals?.gross ? Number(item.rightTotals.gross).toFixed(3) : '0.000'}</td>
                                                            </tr>

                                                            <tr className='bg-light border-top border-dark border-1'>
                                                                <td className="text-center fw-bold align-middle">S.No</td>
                                                                <td className="text-center fw-bold align-middle" colSpan={3}>Count No</td>
                                                                <td className="text-center fw-bold align-middle" colSpan={2}>Bag Range</td>
                                                                <td className="text-center fw-bold align-middle" colSpan={1}>Bags</td>
                                                                <td className="text-center fw-bold align-middle" colSpan={1}>Tare Wt</td>
                                                                <td className="text-center fw-bold align-middle" colSpan={2}>Bag Wt</td>
                                                                <td className="text-center fw-bold align-middle" colSpan={2}>Gross Wt</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="text-center align-middle">1</td>
                                                                <td className="text-center align-middle" colSpan={3}>
                                                                    {(item.duplicateFlag === true || item.duplicateFlag == 1) && (
                                                                        <Icon icon='Warning' color='dark' className="me-1 align-middle" style={{ verticalAlign: 'middle' }} />
                                                                    )}
                                                                    {item.countNo}
                                                                </td>
                                                                <td className="text-center align-middle" colSpan={2}>{item.fromBagNo} - {item.toBagNo}</td>
                                                                <td className="text-center align-middle" colSpan={1}>{item.productList?.length || 0}</td>
                                                                <td className="text-center align-middle" colSpan={1}>{item.totalTareWeight ? Number(item.totalTareWeight).toFixed(3) : '0.000'}</td>
                                                                <td className="text-center align-middle" colSpan={2}>{item.totalNetWeight ? Number(item.totalNetWeight).toFixed(3) : '0.000'}</td>
                                                                <td className="text-center align-middle" colSpan={2}>{item.totalGrossWeight ? Number(item.totalGrossWeight).toFixed(3) : '0.000'}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className='text-center fw-bold text-danger py-4'>
                                    {noDataMsg || "No unscanned products found for the selected criteria."}
                                </div>
                            )}
                        </CardBody>
                        <PaginationButtons
                            data={items}
                            label='items'
                            setCurrentPage={setCurrentPage}
                            currentPage={currentPage}
                            perPage={perPage}
                            setPerPage={setPerPage}
                            className="d-print-none"
                        />
                    </Card>
                )}
            </Page>
        </PageWrapper>
    );
};

export default UnscannedProductReport;